import { useState, useEffect, useRef } from 'react'
import type { FunnelHook } from './useFunnelState'
import type { FunnelData } from '../../lib/types'
import {
  QUALIFIKATIONEN,
  ARBEITSBEREICHE,
  ARBEITSMODELLE,
  BUNDESLAENDER,
  ANERKENNUNGSSTATUS,
  VERFUEGBARKEIT,
} from '../../lib/constants'
import {
  CATEGORY_BY_ID,
  slidersForCategory,
  SWITCH_REASONS,
  SWITCH_REASON_BY_ID,
  NO_GOS,
  type CategoryId,
} from '../../lib/matching/catalog'
import { supabase } from '../../lib/supabase'

let plzData: { plz: string; ort: string; bundesland: string }[] | null = null

interface Props {
  funnel: FunnelHook
  questionId: string
}

export default function Questions({ funnel, questionId }: Props) {
  const { data, updateField, updateFields } = funnel

  if (questionId.startsWith('cat_')) {
    return <QCategory funnel={funnel} categoryId={questionId.slice(4) as CategoryId} />
  }

  switch (questionId) {
    case 'name':
      return <QName data={data} updateField={updateField} />
    case 'kontakt':
      return <QKontakt data={data} updateField={updateField} />
    case 'standort':
      return <QStandort data={data} updateField={updateField} updateFields={updateFields} />
    case 'mobilitaet':
      return <QMobilitaet data={data} updateField={updateField} />
    case 'qualifikation':
      return <QQualifikation data={data} updateField={updateField} />
    case 'anerkennung':
      return <QAnerkennung data={data} updateField={updateField} />
    case 'erfahrung':
      return <QErfahrung data={data} updateField={updateField} />
    case 'wunsch_bereich':
      return <QWunschBereich data={data} updateField={updateField} />
    case 'wunsch_stunden':
      return <QWunschStunden data={data} updateField={updateField} />
    case 'wunsch_zeitpunkt':
      return <QWunschZeitpunkt data={data} updateField={updateField} />
    case 'wunsch_gehalt':
      return <QWunschGehalt data={data} updateField={updateField} />
    case 'wunsch_weg':
      return <QWunschWeg data={data} updateField={updateField} />
    case 'schichten':
      return <QSchichten data={data} updateField={updateField} />
    case 'gruende':
      return <QGruende funnel={funnel} />
    case 'gruende_top':
      return <QGruendeTop funnel={funnel} />
    case 'nogos':
      return <QNoGos data={data} updateField={updateField} />
    case 'begleitung':
      return <QBegleitung data={data} updateField={updateField} />
    case 'freitext':
      return <QFreitext data={data} updateField={updateField} />
    case 'consent':
      return <QConsent funnel={funnel} />
    default:
      return null
  }
}

type UF = <K extends keyof FunnelData>(field: K, value: FunnelData[K]) => void
type QProps = { data: FunnelData; updateField: UF }

/* ============================================
   GEMEINSAME BAUSTEINE
   ============================================ */

function BipolarSlider({
  value,
  onChange,
  minLabel,
  maxLabel,
}: {
  value: number
  onChange: (v: number) => void
  minLabel: string
  maxLabel: string
}) {
  return (
    <div className="bp-slider">
      <input
        type="range"
        className="funnel-slider"
        min={-5}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
      />
      <div className="bp-slider-ends">
        <span>{minLabel}</span>
        <span className="bp-slider-mid">neutral</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  )
}

function TriToggle({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="toggle-group">
      {VERFUEGBARKEIT.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`toggle-btn ${value === o.value ? 'toggle-btn--selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* ============================================
   PHASE 1 — GRUNDDATEN
   ============================================ */

function QName({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="f-vorname">Vorname *</label>
        <input id="f-vorname" className="funnel-input" type="text" placeholder="Vorname" value={data.vorname} onChange={(e) => updateField('vorname', e.target.value)} autoComplete="given-name" autoFocus />
      </div>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="f-nachname">Nachname *</label>
        <input id="f-nachname" className="funnel-input" type="text" placeholder="Nachname" value={data.nachname} onChange={(e) => updateField('nachname', e.target.value)} autoComplete="family-name" />
      </div>
    </>
  )
}

const COUNTRY_CODES = [
  { code: '+49', flag: '🇩🇪' },
  { code: '+43', flag: '🇦🇹' },
  { code: '+41', flag: '🇨🇭' },
  { code: '+48', flag: '🇵🇱' },
  { code: '+40', flag: '🇷🇴' },
  { code: '+385', flag: '🇭🇷' },
  { code: '+90', flag: '🇹🇷' },
] as const

function QKontakt({ data, updateField }: QProps) {
  const selectedCountry = COUNTRY_CODES.find((c) => c.code === data.telefon_vorwahl) || COUNTRY_CODES[0]
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="f-telefon">Telefonnummer</label>
        <div className="phone-input-row">
          <div className="phone-country-select">
            <select className="phone-country-dropdown" value={data.telefon_vorwahl} onChange={(e) => updateField('telefon_vorwahl', e.target.value)} title="Ländervorwahl">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <span className="phone-country-display">{selectedCountry.flag} {selectedCountry.code}</span>
          </div>
          <input id="f-telefon" className="funnel-input phone-number-input" type="tel" placeholder="170 1234567" value={data.telefon} onChange={(e) => updateField('telefon', e.target.value.replace(/[^\d\s]/g, ''))} autoComplete="tel-national" autoFocus />
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="f-email">E-Mail-Adresse *</label>
        <input id="f-email" className="funnel-input" type="email" placeholder="ihre@email.de" value={data.email} onChange={(e) => updateField('email', e.target.value)} autoComplete="email" />
      </div>
    </>
  )
}

function QStandort({ data, updateField, updateFields }: QProps & { updateFields: (f: Partial<FunnelData>) => void }) {
  const [suggestions, setSuggestions] = useState<{ plz: string; ort: string; bundesland: string }[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function loadPlz() {
    if (plzData) return plzData
    try {
      const mod = await import('../../data/plz.json')
      plzData = mod.default as { plz: string; ort: string; bundesland: string }[]
      return plzData
    } catch {
      return null
    }
  }

  async function handlePlzChange(value: string) {
    updateField('plz', value)
    if (value.length >= 2) {
      const d = await loadPlz()
      if (d) {
        const m = d.filter((x) => x.plz.startsWith(value)).slice(0, 8)
        setSuggestions(m)
        setShowDropdown(m.length > 0)
      }
    } else {
      setSuggestions([])
      setShowDropdown(false)
    }
  }

  function selectPlz(entry: { plz: string; ort: string; bundesland: string }) {
    updateFields({ plz: entry.plz, wohnort: entry.ort, bundesland: entry.bundesland })
    setShowDropdown(false)
  }

  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label" htmlFor="f-plz">Postleitzahl</label>
        <div className="plz-autocomplete" ref={ref}>
          <input id="f-plz" className="funnel-input" type="text" placeholder="z.B. 10115" value={data.plz} onChange={(e) => handlePlzChange(e.target.value)} autoComplete="postal-code" inputMode="numeric" autoFocus />
          {showDropdown && (
            <div className="plz-suggestions">
              {suggestions.map((s, i) => (
                <div key={i} className="plz-suggestion" onClick={() => selectPlz(s)}>
                  <span><strong>{s.plz}</strong> {s.ort}</span>
                  <span className="plz-suggestion-ort">{BUNDESLAENDER.find((b) => b.value === s.bundesland)?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="funnel-input-row">
        <div className="funnel-field">
          <label className="funnel-label" htmlFor="f-wohnort">Wohnort</label>
          <input id="f-wohnort" className="funnel-input" type="text" placeholder="Ort" value={data.wohnort} onChange={(e) => updateField('wohnort', e.target.value)} />
        </div>
        <div className="funnel-field">
          <label className="funnel-label" htmlFor="f-bundesland">Bundesland</label>
          <select id="f-bundesland" className="funnel-input" value={data.bundesland} onChange={(e) => updateField('bundesland', e.target.value)}>
            <option value="">Auswählen...</option>
            {BUNDESLAENDER.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
      </div>
    </>
  )
}

function QMobilitaet({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label">Haben Sie einen Führerschein?</label>
        <div className="toggle-group">
          <button type="button" className={`toggle-btn ${data.fuehrerschein === true ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('fuehrerschein', true)}>✅ Ja</button>
          <button type="button" className={`toggle-btn ${data.fuehrerschein === false ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('fuehrerschein', false)}>❌ Nein</button>
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Haben Sie einen eigenen PKW?</label>
        <div className="toggle-group">
          <button type="button" className={`toggle-btn ${data.eigener_pkw === true ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('eigener_pkw', true)}>🚗 Ja</button>
          <button type="button" className={`toggle-btn ${data.eigener_pkw === false ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('eigener_pkw', false)}>❌ Nein</button>
        </div>
      </div>
    </>
  )
}

/* ============================================
   PHASE 2 — BERUF & QUALIFIKATION
   ============================================ */

function QQualifikation({ data, updateField }: QProps) {
  return (
    <>
      <div className="select-cards">
        {QUALIFIKATIONEN.map((qu) => (
          <div key={qu.value} className={`select-card ${data.qualifikation === qu.value ? 'select-card--selected' : ''}`} onClick={() => updateField('qualifikation', qu.value)}>
            <span className="select-card-icon">{qu.icon}</span>
            <span className="select-card-label">{qu.label}</span>
          </div>
        ))}
      </div>
      {data.qualifikation === 'sonstiges' && (
        <div className="funnel-field" style={{ marginTop: 'var(--space-4)' }}>
          <input className="funnel-input" type="text" placeholder="Welche Qualifikation genau?" value={data.qualifikation_sonstiges} onChange={(e) => updateField('qualifikation_sonstiges', e.target.value)} autoFocus />
        </div>
      )}
    </>
  )
}

function QAnerkennung({ data, updateField }: QProps) {
  return (
    <>
      <p className="question-hint">Besonders relevant bei einer Ausbildung aus dem Ausland.</p>
      <div className="option-list">
        {ANERKENNUNGSSTATUS.map((a) => (
          <button key={a.value} type="button" className={`option-row ${data.anerkennungsstatus === a.value ? 'option-row--selected' : ''}`} onClick={() => updateField('anerkennungsstatus', a.value)}>
            <span>{a.label}</span>
            <span className="option-check">✓</span>
          </button>
        ))}
      </div>
    </>
  )
}

function QErfahrung({ data, updateField }: QProps) {
  return (
    <div className="funnel-field">
      <div className="funnel-slider-wrapper">
        <input type="range" className="funnel-slider" min="0" max="40" step="1" value={data.berufserfahrung_jahre ?? 0} onChange={(e) => updateField('berufserfahrung_jahre', parseInt(e.target.value))} />
        <div className="slider-value slider-value--lg">
          {data.berufserfahrung_jahre ?? 0} {(data.berufserfahrung_jahre ?? 0) === 1 ? 'Jahr' : 'Jahre'}
        </div>
        <div className="slider-labels"><span>Einsteiger</span><span>40+ Jahre</span></div>
      </div>
    </div>
  )
}

function QWunschBereich({ data, updateField }: QProps) {
  const toggle = (value: string) => {
    const cur = data.gewuenschte_bereiche || []
    updateField('gewuenschte_bereiche', cur.includes(value) ? cur.filter((b) => b !== value) : [...cur, value])
  }
  return (
    <>
      <p className="question-hint">Mehrfachauswahl möglich</p>
      <div className="select-cards">
        {ARBEITSBEREICHE.filter((b) => b.value !== 'sonstiges').map((b) => (
          <div key={b.value} className={`select-card ${(data.gewuenschte_bereiche || []).includes(b.value) ? 'select-card--selected' : ''}`} onClick={() => toggle(b.value)}>
            <span className="select-card-icon">{b.icon}</span>
            <span className="select-card-label">{b.label}</span>
          </div>
        ))}
      </div>
    </>
  )
}

/* ============================================
   PHASE 3 — ARBEITSZEIT & RAHMEN
   ============================================ */

function QWunschStunden({ data, updateField }: QProps) {
  const toggleModell = (value: string) => {
    const cur = data.gewuenschte_arbeitsmodelle || []
    updateField('gewuenschte_arbeitsmodelle', cur.includes(value) ? cur.filter((m) => m !== value) : [...cur, value])
  }
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label">Gewünschte Wochenstunden</label>
        <div className="funnel-slider-wrapper">
          <input type="range" className="funnel-slider" min="5" max="45" step="1" value={data.gewuenschte_wochenstunden ?? 35} onChange={(e) => updateField('gewuenschte_wochenstunden', parseInt(e.target.value))} />
          <div className="slider-value slider-value--lg">{data.gewuenschte_wochenstunden ?? 35} Std./Woche</div>
          <div className="slider-labels"><span>5 Std.</span><span>45 Std.</span></div>
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Gewünschtes Arbeitsmodell <span className="funnel-label-hint">— Mehrfachauswahl</span></label>
        <div className="chip-group">
          {ARBEITSMODELLE.map((m) => (
            <button key={m.value} type="button" className={`chip-btn ${(data.gewuenschte_arbeitsmodelle || []).includes(m.value) ? 'chip-btn--selected' : ''}`} onClick={() => toggleModell(m.value)}>
              {m.icon} {m.label}<span className="chip-check">✓</span>
            </button>
          ))}
        </div>
      </div>
    </>
  )
}

function QWunschZeitpunkt({ data, updateField }: QProps) {
  const today = new Date().toISOString().split('T')[0]
  function setQuickDate(months: number) {
    const d = new Date()
    d.setMonth(d.getMonth() + months)
    updateField('fruehester_wechselzeitpunkt', d.toISOString().split('T')[0])
  }
  return (
    <>
      <div className="quick-options">
        <button type="button" className={`quick-option ${data.fruehester_wechselzeitpunkt === today ? 'quick-option--selected' : ''}`} onClick={() => updateField('fruehester_wechselzeitpunkt', today)}>Ab sofort</button>
        <button type="button" className="quick-option" onClick={() => setQuickDate(1)}>In 1 Monat</button>
        <button type="button" className="quick-option" onClick={() => setQuickDate(3)}>In 3 Monaten</button>
        <button type="button" className="quick-option" onClick={() => setQuickDate(6)}>In 6 Monaten</button>
      </div>
      <input className="funnel-input" type="date" min={today} value={data.fruehester_wechselzeitpunkt} onChange={(e) => updateField('fruehester_wechselzeitpunkt', e.target.value)} />
    </>
  )
}

function QWunschGehalt({ data, updateField }: QProps) {
  return (
    <>
      <p className="question-hint">Beides hilft uns, nur wirklich passende Stellen vorzuschlagen.</p>
      <div className="funnel-field">
        <label className="funnel-label">Mindestgehalt (brutto/Monat) — darunter kommt nichts in Frage</label>
        <div className="funnel-slider-wrapper">
          <input type="range" className="funnel-slider" min="1500" max="6000" step="100" value={data.mindestgehalt_brutto ?? 2800} onChange={(e) => updateField('mindestgehalt_brutto', parseInt(e.target.value))} />
          <div className="slider-value slider-value--lg">{(data.mindestgehalt_brutto ?? 2800).toLocaleString('de-DE')} €</div>
          <div className="slider-labels"><span>1.500 €</span><span>6.000 €</span></div>
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Wunschgehalt (brutto/Monat)</label>
        <div className="funnel-slider-wrapper">
          <input type="range" className="funnel-slider" min="1500" max="6000" step="100" value={data.gehaltswunsch_brutto ?? 3400} onChange={(e) => updateField('gehaltswunsch_brutto', parseInt(e.target.value))} />
          <div className="slider-value slider-value--lg">{(data.gehaltswunsch_brutto ?? 3400).toLocaleString('de-DE')} €</div>
          <div className="slider-labels"><span>1.500 €</span><span>6.000 €</span></div>
        </div>
      </div>
    </>
  )
}

function QWunschWeg({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label">Maximaler Arbeitsweg</label>
        <div className="funnel-slider-wrapper">
          <input type="range" className="funnel-slider" min="5" max="80" step="5" value={data.max_arbeitsweg_km ?? 25} onChange={(e) => updateField('max_arbeitsweg_km', parseInt(e.target.value))} />
          <div className="slider-value slider-value--lg">max. {data.max_arbeitsweg_km ?? 25} km</div>
          <div className="slider-labels"><span>5 km</span><span>80 km</span></div>
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Wäre ein Umzug für die richtige Stelle eine Option?</label>
        <div className="toggle-group">
          <button type="button" className={`toggle-btn ${data.umzug_moeglich === true ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('umzug_moeglich', true)}>📦 Ja</button>
          <button type="button" className={`toggle-btn ${data.umzug_moeglich === false ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('umzug_moeglich', false)}>🏠 Nein</button>
        </div>
      </div>
      {data.umzug_moeglich === true && (
        <div className="funnel-field">
          <label className="funnel-label" htmlFor="f-umzug">Wohin könnten Sie sich vorstellen umzuziehen?</label>
          <input id="f-umzug" className="funnel-input" type="text" placeholder="z.B. Raum München, Norddeutschland, flexibel..." value={data.umzug_wohin} onChange={(e) => updateField('umzug_wohin', e.target.value)} autoFocus />
        </div>
      )}
    </>
  )
}

function QSchichten({ data, updateField }: QProps) {
  const rows: { field: keyof FunnelData; label: string }[] = [
    { field: 'nachtdienst_moeglich', label: 'Nachtdienst' },
    { field: 'wochenenddienst_moeglich', label: 'Wochenenddienst' },
    { field: 'geteilte_dienste_ok', label: 'Geteilte Dienste' },
    { field: 'rufbereitschaft_ok', label: 'Rufbereitschaft' },
  ]
  return (
    <>
      <p className="question-hint">Geben Sie ehrlich an, was für Sie machbar ist.</p>
      {rows.map((r) => (
        <div key={r.field} className="funnel-field">
          <label className="funnel-label">{r.label}</label>
          <TriToggle value={(data[r.field] as string) || ''} onChange={(v) => updateField(r.field, v as FunnelData[typeof r.field])} />
        </div>
      ))}
    </>
  )
}

/* ============================================
   PHASE 4 — WECHSELGRÜNDE
   ============================================ */

function QGruende({ funnel }: { funnel: FunnelHook }) {
  const { data, updateFields } = funnel

  function toggleReason(id: string) {
    const selected = data.wechselgruende.includes(id)
    if (selected) {
      const intens = { ...data.wechselgrund_intensitaet }
      delete intens[id]
      updateFields({
        wechselgruende: data.wechselgruende.filter((g) => g !== id),
        wechselgrund_intensitaet: intens,
        wechselgrund_top: data.wechselgrund_top.filter((g) => g !== id),
      })
    } else {
      updateFields({
        wechselgruende: [...data.wechselgruende, id],
        wechselgrund_intensitaet: { ...data.wechselgrund_intensitaet, [id]: 3 },
      })
    }
  }

  function setIntensity(id: string, value: number) {
    updateFields({ wechselgrund_intensitaet: { ...data.wechselgrund_intensitaet, [id]: value } })
  }

  return (
    <>
      <p className="question-hint">Wählen Sie alle zutreffenden Gründe — und wie stark sie Sie belasten.</p>
      <div className="reason-list">
        {SWITCH_REASONS.map((r) => {
          const selected = data.wechselgruende.includes(r.id)
          const intensity = data.wechselgrund_intensitaet[r.id] ?? 3
          return (
            <div key={r.id} className={`reason-card ${selected ? 'reason-card--selected' : ''}`}>
              <button type="button" className="reason-card-head" onClick={() => toggleReason(r.id)}>
                <span className="reason-icon">{r.icon}</span>
                <span className="reason-label">{r.label}</span>
                <span className={`reason-toggle ${selected ? 'reason-toggle--on' : ''}`}>{selected ? '✓' : '+'}</span>
              </button>
              {selected && (
                <div className="reason-intensity">
                  <input type="range" className="funnel-slider" min={1} max={5} step={1} value={intensity} onChange={(e) => setIntensity(r.id, parseInt(e.target.value))} />
                  <div className="reason-intensity-scale">
                    <span>stört etwas</span>
                    <strong>{intensity} / 5</strong>
                    <span>Hauptgrund</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}

function QGruendeTop({ funnel }: { funnel: FunnelHook }) {
  const { data, updateField } = funnel
  const selected = data.wechselgruende

  if (selected.length === 0) {
    return (
      <div className="empty-hint" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
        Sie haben noch keine Wechselgründe gewählt. Gehen Sie einen Schritt zurück.
      </div>
    )
  }

  function toggleTop(id: string) {
    const top = data.wechselgrund_top
    if (top.includes(id)) {
      updateField('wechselgrund_top', top.filter((g) => g !== id))
    } else if (top.length < 3) {
      updateField('wechselgrund_top', [...top, id])
    }
  }

  return (
    <>
      <p className="question-hint">Tippen Sie Ihre 3 wichtigsten Gründe an — die Reihenfolge zählt.</p>
      <div className="reason-list">
        {selected.map((id) => {
          const r = SWITCH_REASON_BY_ID[id]
          if (!r) return null
          const rank = data.wechselgrund_top.indexOf(id)
          const isTop = rank >= 0
          return (
            <button
              key={id}
              type="button"
              className={`reason-card reason-card--pick ${isTop ? 'reason-card--selected' : ''}`}
              onClick={() => toggleTop(id)}
            >
              <span className="reason-card-head" style={{ cursor: 'pointer' }}>
                <span className="reason-rank">{isTop ? `#${rank + 1}` : ''}</span>
                <span className="reason-icon">{r.icon}</span>
                <span className="reason-label">{r.label}</span>
              </span>
            </button>
          )
        })}
      </div>
      <p className="question-hint" style={{ marginTop: 'var(--space-4)' }}>
        {data.wechselgrund_top.length} / 3 ausgewählt
      </p>
    </>
  )
}

/* ============================================
   PHASE 5 — SLIDER-KATEGORIEN
   ============================================ */

function QCategory({ funnel, categoryId }: { funnel: FunnelHook; categoryId: CategoryId }) {
  const { data, setSlider } = funnel
  const category = CATEGORY_BY_ID[categoryId]
  const sliders = slidersForCategory(categoryId)

  return (
    <>
      <p className="question-hint">{category.candidateIntro}</p>
      <div className="slider-q-list">
        {sliders.map((s) => {
          const value = data.slider_antworten[s.id] ?? 0
          return (
            <div key={s.id} className="slider-q">
              <div className="slider-q-head">
                <label className="slider-q-label">{s.labelCandidate}</label>
                <span className={`slider-q-val ${value !== 0 ? 'slider-q-val--set' : ''}`}>
                  {value > 0 ? '+' : ''}{value}
                </span>
              </div>
              <BipolarSlider
                value={value}
                onChange={(v) => setSlider(s.id, v)}
                minLabel={s.candidateMinLabel}
                maxLabel={s.candidateMaxLabel}
              />
            </div>
          )
        })}
      </div>
    </>
  )
}

/* ============================================
   PHASE 6 — NO-GOS & BEGLEITUNG
   ============================================ */

function QNoGos({ data, updateField }: QProps) {
  const toggle = (id: string) => {
    const cur = data.no_gos || []
    updateField('no_gos', cur.includes(id) ? cur.filter((n) => n !== id) : [...cur, id])
  }
  return (
    <>
      <p className="question-hint">Was kommt für Sie absolut nicht in Frage? Mehrfachauswahl möglich.</p>
      <div className="chip-group chip-group--lg">
        {NO_GOS.map((n) => (
          <button key={n.id} type="button" className={`chip-btn chip-btn--lg ${(data.no_gos || []).includes(n.id) ? 'chip-btn--selected' : ''}`} onClick={() => toggle(n.id)}>
            <span className="chip-icon-lg">{n.icon}</span>
            {n.label}
            <span className="chip-check">✓</span>
          </button>
        ))}
      </div>
    </>
  )
}

function QBegleitung({ data, updateField }: QProps) {
  return (
    <>
      <p className="question-hint">Medi-Lane begleitet Sie durch den gesamten Wechsel. Was wünschen Sie sich?</p>
      <div className="funnel-field">
        <label className="funnel-label">Möchten Sie vorab in einer Einrichtung hospitieren?</label>
        <div className="toggle-group">
          {[['ja', 'Ja'], ['vielleicht', 'Vielleicht'], ['nein', 'Nein']].map(([v, l]) => (
            <button key={v} type="button" className={`toggle-btn ${data.trial_day_interesse === v ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('trial_day_interesse', v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Unterstützung bei einem Umzug nötig?</label>
        <div className="toggle-group">
          {[['ja', 'Ja'], ['vielleicht', 'Vielleicht'], ['nein', 'Nein']].map(([v, l]) => (
            <button key={v} type="button" className={`toggle-btn ${data.umzugs_support === v ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('umzugs_support', v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Unterstützung bei Anerkennung / Behörden nötig?</label>
        <div className="toggle-group">
          {[['ja', 'Ja'], ['vielleicht', 'Vielleicht'], ['nein', 'Nein']].map(([v, l]) => (
            <button key={v} type="button" className={`toggle-btn ${data.anerkennungs_support === v ? 'toggle-btn--selected' : ''}`} onClick={() => updateField('anerkennungs_support', v)}>{l}</button>
          ))}
        </div>
      </div>
    </>
  )
}

function QFreitext({ data, updateField }: QProps) {
  return (
    <>
      <div className="funnel-field">
        <label className="funnel-label">Was soll auf keinen Fall wieder passieren? <span className="funnel-label-hint">— optional</span></label>
        <textarea className="funnel-textarea" placeholder="Erzählen Sie uns, was Sie nicht wieder erleben möchten..." value={data.freitext_ausschluss} onChange={(e) => updateField('freitext_ausschluss', e.target.value)} maxLength={500} rows={4} />
        <div className="char-counter">{data.freitext_ausschluss.length} / 500</div>
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Was macht Ihren Traumjob aus? <span className="funnel-label-hint">— optional</span></label>
        <textarea className="funnel-textarea" placeholder="Beschreiben Sie, was Ihnen besonders wichtig ist..." value={data.freitext_wichtig} onChange={(e) => updateField('freitext_wichtig', e.target.value)} maxLength={500} rows={4} />
        <div className="char-counter">{data.freitext_wichtig.length} / 500</div>
      </div>
    </>
  )
}

/* ============================================
   PHASE 7 — ABSCHLUSS
   ============================================ */

function QConsent({ funnel }: { funnel: FunnelHook }) {
  const { data, updateField, markSubmitted } = funnel
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = data.einwilligung_datenschutz && data.einwilligung_kontakt && !!data.vorname && !!data.email

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const pkId = crypto.randomUUID()
      const { error: insertError } = await supabase
        .from('pflegekraefte')
        .insert({
          id: pkId,
          vorname: data.vorname,
          nachname: data.nachname,
          telefon_vorwahl: data.telefon_vorwahl || '+49',
          telefon: data.telefon ? `${data.telefon_vorwahl} ${data.telefon}` : null,
          email: data.email,
          plz: data.plz || null,
          wohnort: data.wohnort || null,
          bundesland: data.bundesland || null,
          fuehrerschein: data.fuehrerschein,
          eigener_pkw: data.eigener_pkw,
          qualifikation: data.qualifikation || null,
          qualifikation_sonstiges: data.qualifikation_sonstiges || null,
          anerkennungsstatus: data.anerkennungsstatus || null,
          berufserfahrung_jahre: data.berufserfahrung_jahre,
          gewuenschter_bereich: data.gewuenschte_bereiche[0] || null,
          gewuenschte_bereiche: data.gewuenschte_bereiche.length ? data.gewuenschte_bereiche : null,
          gewuenschte_wochenstunden: data.gewuenschte_wochenstunden,
          gewuenschtes_arbeitsmodell: data.gewuenschte_arbeitsmodelle[0] || null,
          gewuenschte_arbeitsmodelle: data.gewuenschte_arbeitsmodelle.length ? data.gewuenschte_arbeitsmodelle : null,
          fruehester_wechselzeitpunkt: data.fruehester_wechselzeitpunkt || null,
          max_arbeitsweg_km: data.max_arbeitsweg_km,
          umzug_moeglich: data.umzug_moeglich,
          umzug_wohin: data.umzug_wohin || null,
          mindestgehalt_brutto: data.mindestgehalt_brutto,
          gehaltswunsch_brutto: data.gehaltswunsch_brutto,
          nachtdienst_moeglich: data.nachtdienst_moeglich || null,
          wochenenddienst_moeglich: data.wochenenddienst_moeglich || null,
          geteilte_dienste_ok: data.geteilte_dienste_ok || null,
          rufbereitschaft_ok: data.rufbereitschaft_ok || null,
          no_gos: data.no_gos.length ? data.no_gos : null,
          slider_antworten: data.slider_antworten,
          trial_day_interesse: data.trial_day_interesse || null,
          umzugs_support: data.umzugs_support || null,
          anerkennungs_support: data.anerkennungs_support || null,
          freitext_ausschluss: data.freitext_ausschluss || null,
          freitext_wichtig: data.freitext_wichtig || null,
          einwilligung_datenschutz: data.einwilligung_datenschutz,
          einwilligung_kontakt: data.einwilligung_kontakt,
          einwilligung_anonymisiert: data.einwilligung_anonymisiert,
          status: 'neu',
        })

      if (insertError) throw insertError

      if (data.wechselgruende.length > 0) {
        const rows = data.wechselgruende.map((g) => {
          const topIndex = data.wechselgrund_top.indexOf(g)
          return {
            pflegekraft_id: pkId,
            grund: g,
            intensitaet: data.wechselgrund_intensitaet[g] ?? 0,
            top_rang: topIndex >= 0 ? topIndex + 1 : null,
          }
        })
        const { error: wgError } = await supabase.from('pflegekraft_wechselgruende').insert(rows)
        if (wgError) throw wgError
      }

      markSubmitted()
    } catch (err) {
      console.error('Submit error:', err)
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
      setSubmitting(false)
    }
  }

  return (
    <>
      <p className="question-hint">Bitte bestätigen Sie die folgenden Einwilligungen.</p>
      <div className="consent-group">
        <div className="consent-item" onClick={() => updateField('einwilligung_datenschutz', !data.einwilligung_datenschutz)}>
          <div className={`consent-checkbox ${data.einwilligung_datenschutz ? 'consent-checkbox--checked' : ''}`}>{data.einwilligung_datenschutz && '✓'}</div>
          <div>
            <span className="consent-label"><strong>Datenschutzerklärung *</strong><br />Ich habe die Datenschutzerklärung gelesen und bin mit der Verarbeitung meiner Daten einverstanden.</span>
            {!data.einwilligung_datenschutz && <><br /><span className="consent-required">Pflichtfeld</span></>}
          </div>
        </div>
        <div className="consent-item" onClick={() => updateField('einwilligung_kontakt', !data.einwilligung_kontakt)}>
          <div className={`consent-checkbox ${data.einwilligung_kontakt ? 'consent-checkbox--checked' : ''}`}>{data.einwilligung_kontakt && '✓'}</div>
          <div>
            <span className="consent-label"><strong>Kontaktaufnahme *</strong><br />Medi-Lane darf mich bezüglich passender Stellenangebote kontaktieren.</span>
            {!data.einwilligung_kontakt && <><br /><span className="consent-required">Pflichtfeld</span></>}
          </div>
        </div>
        <div className="consent-item" onClick={() => updateField('einwilligung_anonymisiert', !data.einwilligung_anonymisiert)}>
          <div className={`consent-checkbox ${data.einwilligung_anonymisiert ? 'consent-checkbox--checked' : ''}`}>{data.einwilligung_anonymisiert && '✓'}</div>
          <div><span className="consent-label"><strong>Anonymisierte Vorstellung</strong> <span className="funnel-label-hint">— optional</span><br />Mein Profil darf anonymisiert bei passenden Arbeitgebern vorgestellt werden.</span></div>
        </div>
      </div>

      {error && <div className="submit-error animate-shake">⚠️ {error}</div>}

      <div className="funnel-nav" style={{ marginTop: 'var(--space-4)' }}>
        <button className="funnel-btn funnel-btn--secondary" onClick={funnel.prev}>← Zurück</button>
        <button className="funnel-btn funnel-btn--primary funnel-btn--submit" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? '⏳ Wird gesendet...' : '✅ Profil absenden'}
        </button>
      </div>
    </>
  )
}
